<i18n src="./resources/locales.json"></i18n>

<template>
  <v-dialog :value="isDialogOpen" max-width="75%" persistent>
    <v-card id="assignments-dialog" class="surface">
      <v-card-title class="surface lighten-1 pa-0 pt-4 d-block">
        <div class="pl-4 pb-4">{{ $t('assignments') }}</div>
        <v-divider />
        <v-tabs v-model="tab" background-color="transparent">
          <v-tab v-once>
            {{ $t('businessProcess') }}
          </v-tab>
          <v-tab v-once>
            {{ $t('campaign') }}
          </v-tab>
        </v-tabs>
      </v-card-title>

      <v-divider />

      <v-card-text class="surface">
        <v-tabs-items v-model="tab" class="surface">
          <v-tab-item class="tab-content">
            <div>
              <v-subheader class="group-subheader pa-0 mt-0">
                <b>{{ $t('assignmentExpirationInHours') }}</b>
              </v-subheader>
              <v-card-text>
                <v-slider
                  v-model="assignmentExpirationHours"
                  :value="assignmentExpirationHours"
                  class="hours-slider"
                  step="1"
                  :max="12"
                  :min="1"
                  ticks="always"
                  :tick-labels="ticksLabels"
                >
                  <template v-slot:thumb-label="{ value }"> {{ value }} </template>
                  <template v-slot:append>
                    {{ $t('assignmentsWillBeAppliedUntil', { until: untilText, now: nowText }) }}
                  </template>
                </v-slider>
              </v-card-text>

              <div class="assignment-columns" :dark="$vuetify.theme.dark">
                <div class="assignment-column">
                  <v-subheader class="group-subheader pa-0 mt-0">
                    {{ $t('businessProcesses') }}
                  </v-subheader>

                  <div class="fields-container">
                    <div v-for="bp in bps" :id="bp.id" :key="bp.id">
                      <div class="header-item d-flex flex-row align-center">
                        <button
                          :class="selectedBp && bp.id === selectedBp.id ? 'selected-value' : ''"
                          @click="selectBp(bp)"
                        >
                          {{ bp.name }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="assignment-column">
                  <v-subheader class="group-subheader pa-0 mt-0">
                    {{ $t('activeAgents') }}
                  </v-subheader>

                  <div class="fields-container">
                    <div v-for="activeAgent in activeAgents" :id="activeAgent.id" :key="activeAgent.id">
                      <div
                        class="header-item d-flex flex-row align-center"
                        :class="activeAgent.expirationTime ? 'expires' : ''"
                      >
                        <button
                          :class="selectedAgent && activeAgent.id === selectedAgent.id ? 'selected-value' : ''"
                          @click="selectAgent(activeAgent)"
                        >
                          {{ activeAgent.name }}

                          <span v-if="activeAgent.expirationTime">{{ formatDate(activeAgent.expirationTime) }}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="assignment-column">
                  <div class="buttons-container">
                    <v-btn
                      depressed
                      class="action-btn primary"
                      :disabled="!selectedAgent || selectedAgent.isActive"
                      @click="switchToActive"
                    >
                      {{ $t('addAgent') }}
                    </v-btn>

                    <v-btn
                      depressed
                      class="action-btn primary"
                      :disabled="!selectedAgent || !selectedAgent.isActive"
                      @click="switchToInactive"
                    >
                      {{ $t('removeAgent') }}
                    </v-btn>

                    <v-btn depressed class="action-btn primary" :disabled="!hasBeenModified" @click="saveChanges">
                      {{ $t('saveChange') }}
                    </v-btn>

                    <v-btn
                      depressed
                      class="action-btn primary"
                      :disabled="tempAgentsCount === 0"
                      @click="setNewExpiration"
                    >
                      {{ $t('setNewExpiration') }}
                    </v-btn>

                    <v-btn depressed class="action-btn primary" :disabled="tempAgentsCount === 0" @click="reset">
                      {{ $t('reset') }}
                    </v-btn>
                  </div>
                </div>
                <div class="assignment-column">
                  <v-subheader class="group-subheader pa-0 mt-0">
                    {{ $t('inactiveAgents') }}
                  </v-subheader>

                  <div class="fields-container">
                    <div v-for="inactiveAgent in inactiveAgents" :id="inactiveAgent.id" :key="inactiveAgent.id">
                      <div
                        class="header-item d-flex flex-row align-center"
                        :class="inactiveAgent.expirationTime ? 'expires' : ''"
                      >
                        <button
                          :class="selectedAgent && inactiveAgent.id === selectedAgent.id ? 'selected-value' : ''"
                          @click="selectAgent(inactiveAgent)"
                        >
                          {{ inactiveAgent.name }}
                          <span v-if="inactiveAgent.expirationTime">{{
                            formatDate(inactiveAgent.expirationTime)
                          }}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-tab-item>
          <v-tab-item class="tab-content">
            <EcsCampaignTab
              :tab="tab"
              :now="now"
              :app-time-zone-id="appTimeZoneId"
              :available-campaigns="availableCampaigns"
            />
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>

      <v-divider />

      <v-card-actions class="surface lighten-1">
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="close">
          <span v-once>{{ $t('close') }}</span>
        </v-btn>
      </v-card-actions>
    </v-card>

    <EcsChangeExpirationDialog
      ref="changeExpirationDialog"
      :selected-bp="selectedBp || {}"
      :now="now"
    ></EcsChangeExpirationDialog>
    <EcsConfirmationDialog
      ref="resetAssignmentsConfirmationDialog"
      :title="$t('dialogs.resetBpAssignments.title')"
      :message="$t('dialogs.resetBpAssignments.description', { businessProcessName: selectedBpName })"
      :confirm-button-text="$t('accept')"
      :cancel-button-text="$t('cancel')"
    />
  </v-dialog>
</template>

<script>
import orderBy from 'lodash/orderBy';
import { mapGetters } from 'vuex';
import moment from 'moment-timezone';
import EcsCampaignTab from '@/components/ecs-assignments-dialog/EcsCampaignTab';
import EcsChangeExpirationDialog from '@/components/ecs-assignments-dialog/EcsChangeExpirationDialog';
import EcsConfirmationDialog from '@/components/ecs-confirmation-dialog/EcsConfirmationDialog';
import businessProcessService from '@/services/businessProcessService';
import EcsTimeFormatter from '@/mixins/ecs-time-formatter/EcsTimeFormatter';

const SNACKBAR_MESSAGE_EVENT = 'snackbar-message';

export default {
  name: 'EcsAssignmentsDialog',

  components: { EcsCampaignTab, EcsChangeExpirationDialog, EcsConfirmationDialog },

  mixins: [EcsTimeFormatter],

  props: {},

  data() {
    return {
      isDialogOpen: false,
      isLoading: false,
      tab: 0,
      isAcceptDisabled: false,
      assignmentExpirationHours: 1,
      ticksLabels: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
      agents: [],
      initialAgentsData: [],
      selectedBp: null,
      selectedAgent: null,
      now: moment().tz(this.getIanaTimeZone(this.appTimeZoneId)),
      nowInterval: null
    };
  },

  computed: {
    ...mapGetters('CAMPAIGNS', ['availableCampaigns']),

    ...mapGetters('ORGANIZATION', ['businessProcesses']),

    ...mapGetters('SETTINGS', ['applicationSettings']),

    ...mapGetters('REFERENCES', ['availableTimeZones']),

    assignmentExpirationHoursString() {
      const leadingZero = this.assignmentExpirationHours < 10 ? '0' : '';
      return leadingZero + this.assignmentExpirationHours + ':00:00';
    },

    bps() {
      // const bps = this.businessProcesses;
      const bps = this.availableCampaigns
        .map(campaign => {
          return {
            id: campaign.bpid,
            name: campaign.bpName
          };
        })
        .filter((item1, index, arr) => arr.findIndex(item2 => item2.id === item1.id) === index);
      bps.sort((a, b) => {
        return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
      });
      return bps;
    },

    selectedBpName() {
      return this.selectedBp ? this.selectedBp.name : '';
    },

    activeAgents() {
      if (this.agents) {
        return this.agents.filter(agent => {
          return agent.isActive;
        });
      }
      return [];
    },
    inactiveAgents() {
      if (this.agents) {
        return this.agents.filter(agent => {
          return !agent.isActive;
        });
      }
      return [];
    },

    tempAgentsCount() {
      return this.agents.filter(agent => !!agent.expirationTime).map(agent => agent.id).length;
    },

    hasBeenModified() {
      if (JSON.stringify(this.initialAgentsData) !== JSON.stringify(this.agents)) {
        return true;
      }
      return false;
    },

    appTimeZoneId() {
      return this.availableTimeZones.find(tz => tz.value === this.applicationSettings.timeZoneId).id || {};
    },

    nowText() {
      return this.now.format('MMM D, hh:mm A');
    },

    untilText() {
      const until = moment(this.now);
      return until.add(this.assignmentExpirationHours, 'hours').format('MMM D, hh:mm A');
    }
  },
  sockets: {
    AGENT_BP_ASSIGNMENT_CHANGED_EVENT() {
      if (this.selectedBp) {
        this.selectedAgent = null;
        this.selectBp(this.selectedBp);
      }
    }
  },
  mounted() {
    this.$root.$on('disable-accept', this.disableAccept);
    this.$root.$on('enable-accept', this.enableAccept);
    this.nowInterval = setInterval(this.updateNow, 1000);
  },
  beforeDestroy() {
    clearInterval(this.nowInterval);
  },
  methods: {
    open() {
      this.isDialogOpen = true;
    },

    close() {
      this.isDialogOpen = false;
    },

    disableAccept() {
      this.isAcceptDisabled = true;
    },

    enableAccept() {
      this.isAcceptDisabled = false;
    },

    selectBp(bp) {
      this.selectedBp = bp;

      businessProcessService
        .getBusinessProcessAssignments(bp.id)
        .then(({ data }) => {
          this.agents = orderBy(
            data.map(o => {
              o.hasBeenModified = false;
              return o;
            }),
            [o => o.isActive, o => o.name],
            ['desc', 'asc']
          );
          this.initialAgentsData = structuredClone(this.agents);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    selectAgent(agent) {
      this.selectedAgent = agent;
    },

    switchToInactive() {
      this.selectedAgent.isActive = false;
      this.setAgentToModified();
    },

    switchToActive() {
      this.selectedAgent.isActive = true;
      this.setAgentToModified();
    },

    setAgentToModified() {
      this.selectedAgent.hasBeenModified = !this.selectedAgent.hasBeenModified;
      this.selectedAgent = null;
    },

    saveChanges() {
      this.handleAgentsActivation();
      this.handleAgentsDeactivation();
    },

    handleAgentsActivation() {
      this.isLoading = true;
      const activeAgents = this.activeAgents.filter(agent => agent.hasBeenModified).map(agent => agent.id);
      if (activeAgents.length < 1) {
        return;
      }
      businessProcessService
        .activateAgents(this.selectedBp.id, activeAgents, this.assignmentExpirationHoursString)
        .then(() => {
          this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
            message: this.$t('success.agentsActivated', { businessProcessName: this.selectedBp.name }),
            type: 'success'
          });
        })
        .catch(() => {
          this.isLoading = false;
          this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
            message: this.$t('errors.errorOnAgentsActivation'),
            type: 'error'
          });
        });
    },

    handleAgentsDeactivation() {
      this.isLoading = true;
      const inactiveAgents = this.inactiveAgents.filter(agent => agent.hasBeenModified).map(agent => agent.id);
      if (inactiveAgents.length < 1) {
        return;
      }
      businessProcessService
        .deactivateAgents(this.selectedBp.id, inactiveAgents, this.assignmentExpirationHoursString)
        .then(() => {
          this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
            message: this.$t('success.agentsDeactivated', { businessProcessName: this.selectedBp.name }),
            type: 'success'
          });
        })
        .catch(() => {
          this.isLoading = false;
          this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
            message: this.$t('errors.errorOnAgentsDeactivation'),
            type: 'error'
          });
        });
    },

    setNewExpiration() {
      this.$refs.changeExpirationDialog.open();
    },

    formatDate(date) {
      if (!date) {
        return '';
      }
      const expirationDate = moment(date).tz(this.getIanaTimeZone(this.appTimeZoneId));
      const remainingHours = expirationDate.diff(this.now, 'hours');

      if (remainingHours > 11) {
        return expirationDate.format('MMM D, hh:mm A');
      }
      return expirationDate.format('hh:mm A');
    },

    async reset() {
      const confirmed = await this.$refs.resetAssignmentsConfirmationDialog.open();
      if (confirmed) {
        this.isLoading = true;
        const expirationTime = 0;
        businessProcessService
          .setTemporaryAssignmentsExpiration(this.selectedBp.id, expirationTime)
          .then(() => {
            this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
              message: this.$t('success.resetAgentBpAssignments', { businessProcessName: this.selectedBp.name }),
              type: 'success'
            });
          })
          .catch(() => {
            this.isLoading = false;
            this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
              message: this.$t('errors.errorOnBpResetAssignments'),
              type: 'error'
            });
          });
      }
    },
    updateNow() {
      this.now = moment().tz(this.getIanaTimeZone(this.appTimeZoneId));
    }
  }
};
</script>

<style scoped>
.tab-content {
  height: 500px;
  padding-left: 20px;
}

.loading {
  position: absolute;
}

.hours-slider .v-input__control {
  max-width: 400px;
}

.hours-slider .v-slider__tick-label {
  font-size: 0.8em;
}

.assignment-columns {
  position: relative;
  height: 100%;
  overflow: hidden;
  display: grid;
  grid-template-columns: 3fr 3fr 2fr 3fr;
}

.assignment-column {
  overflow-y: auto;
  padding-left: 5px;
  padding-right: 5px;
}

.assignment-column .v-subheader {
  text-transform: uppercase;
  font-weight: bolder;
}

.assignment-column .selected-value {
  color: var(--v-accent-darken4);
  font-weight: bolder;
}

[dark] .assignment-column .selected-value {
  color: var(--v-accent-base);
}

.fields-container .expires {
  color: var(--v-info-darken4);
}

[dark] .fields-container .expires {
  color: var(--v-info-base);
}

.buttons-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-height: 450px;
  height: 100%;
  gap: 5px;
}

.buttons-container button {
  width: 100%;
}

.fields-container {
  height: 300px;
  max-height: 300px;
  padding: 5px;
  border: 2px solid gray;
  overflow: auto;
}

.fields-container button {
  width: 100%;
  text-align: left;
}

.fields-container button span {
  float: right;
}
</style>

<style>
.hours-slider .v-input__control {
  width: 40%;
}

.hours-slider .v-input__append-outer {
  width: 60%;
}

.buttons-container .v-btn__content {
  white-space: normal;
  max-width: 100%;
}

#assignments-dialog .v-slider__thumb-label-container {
  transition-delay: 0.1s !important;
}
</style>
